import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel } from "../lib/format";
import { Card, Loading, KPI } from "../components/UI";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Arrears() {
  const [arrears, setArrears] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/arrears"),
      api.get("/anomalies/pricing"),
      api.get("/dashboard/totals-check"),
    ]).then(([a, an, c]) => { setArrears(a.data); setAnomalies(an.data); setCheck(c.data); setLoading(false); });
  }, []);

  const confirm = async (unit_id, intentional) => {
    const note = prompt(intentional ? "Note for intentional price difference:" : "Note for correcting:");
    if (note === null) return;
    try {
      await api.post("/anomalies/confirm", { unit_id, intentional, note });
      toast.success("Confirmed");
      const an = await api.get("/anomalies/pricing");
      setAnomalies(an.data);
    } catch { toast.error("Failed"); }
  };

  if (loading) return <Loading/>;
  const overdue = arrears.filter(a => a.is_overdue);
  const totalOutstanding = arrears.reduce((s, a) => s + (a.balance_outstanding || 0), 0);

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Arrears & Data Anomalies</h1>
        <p className="text-[#5E7166] text-sm">Live arrears tracking and price-vs-pricing-table mismatch detection.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Total Outstanding" value={naira(totalOutstanding)} icon={AlertTriangle} accent/>
        <KPI label="Overdue Tenants" value={overdue.length} icon={AlertTriangle}/>
        <KPI label="Pricing Anomalies" value={anomalies.length} icon={AlertTriangle}/>
      </div>

      {check && check.mismatches.length > 0 && (
        <Card title="Live System vs Expected Business Summary">
          <table className="w-full text-sm">
            <thead><tr><th className="text-left text-xs uppercase tracking-wider text-[#5E7166] py-2">Metric</th><th className="text-right">Expected</th><th className="text-right">Live</th><th className="text-right">Difference</th></tr></thead>
            <tbody>
              {check.mismatches.map((m, i) => (
                <tr key={i} className="border-t border-[#E5EBE8]">
                  <td className="py-2">{m.metric}</td>
                  <td className="text-right">{naira(m.expected)}</td>
                  <td className="text-right font-semibold">{naira(m.actual)}</td>
                  <td className={`text-right font-bold ${m.diff < 0 ? "text-[#C92A2A]" : "text-[#0E5C3A]"}`}>{m.diff > 0 ? "+" : ""}{naira(m.diff)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#5E7166] mt-3 bg-[#FDF6E3]/40 p-3 rounded-lg">These differences exist because the seed data contains the Hall ₦1,000,000,000 anomaly. Live totals are calculated from actual records — not forced to match expected.</p>
        </Card>
      )}

      <Card title={`Arrears (${arrears.length} tenants)`} testid="arrears-card">
        <div className="overflow-x-auto">
          <table className="modav-table">
            <thead><tr><th>Tenant</th><th>Unit</th><th>Rent Due</th><th>Paid</th><th>Outstanding</th><th>Days Overdue</th><th>Status</th></tr></thead>
            <tbody>
              {arrears.map(a => (
                <tr key={a.tenant_id} className={a.is_overdue ? "bg-[#FEF0F0]/30" : ""} data-testid={`arrears-${a.tenant_id}`}>
                  <td>{a.tenant_name}</td>
                  <td>{a.unit_name || "—"}</td>
                  <td>{naira(a.rent_due)}</td>
                  <td>{naira(a.total_paid)}</td>
                  <td className="font-serif font-bold">{naira(a.balance_outstanding)}</td>
                  <td>{a.days_overdue}</td>
                  <td>{a.is_overdue ? <span className="badge badge-red">Overdue</span> : a.balance_outstanding > 0 ? <span className="badge badge-gold">Pending</span> : <span className="badge badge-green">Cleared</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title={`Pricing Anomalies (${anomalies.length})`} testid="anomalies-card">
        {anomalies.length === 0 ? <div className="text-center py-6 text-[#5E7166] flex items-center justify-center gap-2"><CheckCircle2 size={18} className="text-[#0E5C3A]"/> No anomalies detected.</div> :
          <ul className="divide-y divide-[#E5EBE8]">
            {anomalies.map((a, i) => (
              <li key={i} className="py-3 flex items-center justify-between gap-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className={a.severity === "high" ? "text-[#C92A2A] mt-0.5" : "text-[#C9A227] mt-0.5"} size={16}/>
                  <div>
                    <div className="font-semibold text-sm">{a.unit_name}</div>
                    <div className="text-xs text-[#5E7166]">{a.message}</div>
                    <span className={`badge ${a.flag === "possible_error" ? "badge-red" : a.flag === "discounted_price" ? "badge-gold" : "badge-blue"} mt-1`}>{statusLabel(a.flag)}</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => confirm(a.unit_id, true)} className="btn-outline-emerald text-xs">Intentional</button>
                  <button onClick={() => confirm(a.unit_id, false)} className="btn-ghost text-xs">Fix Required</button>
                </div>
              </li>
            ))}
          </ul>}
      </Card>
    </div>
  );
}
